import React, { useState } from 'react'
import styled from 'styled-components'

const Section = styled.div`
  margin-top: 2rem;
`

const Title = styled.h2`
  font-size: 1.5rem;
  color: var(--color-primary);
  margin-bottom: 1rem;
`

const WebhookForm = styled.div`
  background: rgba(0, 0, 0, 0.3);
  border-radius: 8px;
  padding: 1.5rem;
  margin-bottom: 1rem;
`

const Input = styled.input`
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  padding: 0.5rem;
  color: var(--color-text);
  font-family: 'Fira Code', monospace;
  font-size: 0.9rem;
  width: 100%;
  margin-bottom: 1rem;

  &:focus {
    outline: none;
    border-color: var(--color-primary);
  }
`

const CheckboxGroup = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
  margin-bottom: 1rem;
`

const Checkbox = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;

  input {
    cursor: pointer;
  }
`

const Button = styled.button`
  background: var(--color-primary);
  color: var(--color-text);
  border: none;
  border-radius: 4px;
  padding: 0.5rem 1rem;
  cursor: pointer;
  font-size: 0.9rem;
  transition: background 0.2s;

  &:hover {
    background: var(--color-primary-dark);
  }

  &:disabled {
    background: rgba(255, 255, 255, 0.1);
    cursor: not-allowed;
  }
`

const WebhookList = styled.div`
  margin-top: 2rem;
`

const WebhookCard = styled.div`
  background: rgba(0, 0, 0, 0.3);
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1rem;

  h3 {
    font-size: 1rem;
    color: var(--color-text);
    margin-bottom: 0.5rem;
  }

  p {
    font-family: 'Fira Code', monospace;
    font-size: 0.9rem;
    color: var(--color-text-secondary);
    margin-bottom: 0.5rem;
  }

  .events {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    margin-top: 0.5rem;
  }
`

const EventTag = styled.span`
  background: rgba(59, 130, 246, 0.1);
  color: var(--color-primary);
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.8rem;
  font-family: 'Fira Code', monospace;
`

export default function WebhookSection() {
  const [webhooks, setWebhooks] = useState([
    {
      id: 'wh_1',
      url: 'https://example.com/webhook',
      events: ['project.created', 'agent.deployed'],
      active: true
    }
  ])

  const [newWebhook, setNewWebhook] = useState({
    url: '',
    events: []
  })

  const eventTypes = [
    'project.created',
    'project.updated',
    'agent.deployed',
    'agent.completed',
    'task.started',
    'task.completed',
    'output.ready',
    'error.occurred'
  ]

  const handleEventToggle = (event) => {
    const events = newWebhook.events.includes(event)
      ? newWebhook.events.filter(e => e !== event)
      : [...newWebhook.events, event]
    setNewWebhook({ ...newWebhook, events })
  }

  const handleSubmit = () => {
    if (newWebhook.url && newWebhook.events.length > 0) {
      setWebhooks([
        ...webhooks,
        {
          id: 'wh_' + (webhooks.length + 1),
          ...newWebhook,
          active: true
        }
      ])
      setNewWebhook({ url: '', events: [] })
    }
  }

  return (
    <Section>
      <Title>Webhooks</Title>
      
      <WebhookForm>
        <Input
          type="url"
          placeholder="Webhook URL"
          value={newWebhook.url}
          onChange={(e) => setNewWebhook({ ...newWebhook, url: e.target.value })}
        />
        
        <h3 style={{ marginBottom: '0.5rem' }}>Events to Subscribe</h3>
        <CheckboxGroup>
          {eventTypes.map(event => (
            <Checkbox key={event}>
              <input
                type="checkbox"
                checked={newWebhook.events.includes(event)}
                onChange={() => handleEventToggle(event)}
              />
              {event}
            </Checkbox>
          ))}
        </CheckboxGroup>

        <Button onClick={handleSubmit} disabled={!newWebhook.url || newWebhook.events.length === 0}>
          Add Webhook
        </Button>
      </WebhookForm>

      <WebhookList>
        <h3 style={{ marginBottom: '1rem' }}>Active Webhooks</h3>
        {webhooks.map(webhook => (
          <WebhookCard key={webhook.id}>
            <h3>{webhook.url}</h3>
            <p>ID: {webhook.id}</p>
            <div className="events">
              {webhook.events.map(event => (
                <EventTag key={event}>{event}</EventTag>
              ))}
            </div>
          </WebhookCard>
        ))}
      </WebhookList>
    </Section>
  )
}
